import React from 'react';
import { DimensionValue, LayoutChangeEvent, StyleProp, ViewStyle } from 'react-native';

export type UseTableCellWidthArgs = {
    tableWidth: number | null;
    columnCount: number;
    minWidth: DimensionValue | null;
};

export type RowIndexOrHeader = { isHeader: true } | { rowIndex: number };

export type GetTableCellLayoutHandlerArgs = {
    colIndex: number;
    contentKey: string;
} & RowIndexOrHeader;

export type GetTableRowLayoutHandlerArgs = {
    rowContentKeys: string[];
} & RowIndexOrHeader;

export type GetCellStyleArgs = {
    colIndex: number;
} & RowIndexOrHeader;

const getRowIndex = (args: RowIndexOrHeader): number => {
    return 'rowIndex' in args ? args.rowIndex : 0;
};

const getNOOP = () => () => {};

export const useTableCellDimensions = ({ minWidth }: UseTableCellWidthArgs) => {
    const [columnWidths, setColumnWidths] = React.useState<number[]>([]);
    const [rowHeights, setRowHeights] = React.useState<number[]>([]);

    /**
     * The layout event rounds down the width.  We have to give the width a buffer because of this.
     * However, this buffer will do an endless loop of re-layouts.  To prevent this, we will keep track of the
     * and only set a columns width once
     */
    const previouslySetColumnWidths = React.useRef<Record<string, true>>({});

    /**
     * The layout event rounds down the height.  We have to give the height a buffer because of this.
     * However, this buffer will do an endless loop of re-layouts.  To prevent this, we will keep track of the
     * and only set a row height once
     */
    const previouslySetColumnHeights = React.useRef<Record<string, true>>({});

    const getTableCellLayoutHandler = (args: GetTableCellLayoutHandlerArgs) => (event: LayoutChangeEvent) => {
        setColumnWidths((prev) => {
            const { colIndex, contentKey } = args;
            const rowIndex = getRowIndex(args);

            if (!event.nativeEvent.layout.width) return prev;

            const computedWidth = event.nativeEvent.layout.width + 1; // account for rounding down

            const getSetColumnWidthKey = () => {
                return `${colIndex}-${rowIndex}-${contentKey}`;
            };

            const isPreviouslySet = previouslySetColumnWidths.current[getSetColumnWidthKey()];
            if (isPreviouslySet) {
                return prev;
            }

            const copiedColumnWidths = [...prev];
            if (!copiedColumnWidths[colIndex] || copiedColumnWidths[colIndex] < computedWidth) {
                copiedColumnWidths[colIndex] = computedWidth;
            }

            previouslySetColumnWidths.current[getSetColumnWidthKey()] = true;
            return copiedColumnWidths;
        });
    };

    const getTableRowLayoutHandler = (args: GetTableRowLayoutHandlerArgs) => (event: LayoutChangeEvent) => {
        setRowHeights((prev) => {
            const { rowContentKeys } = args;
            const rowIndex = getRowIndex(args);

            if (!event.nativeEvent.layout.width) return prev;

            const computedWidth = event.nativeEvent.layout.height + 1; // account for rounding down

            const contentKeys = rowContentKeys.join('-');
            const getSetColumnWidthKey = () => {
                return `${rowIndex}-${contentKeys}`;
            };

            const isPreviouslySet = previouslySetColumnHeights.current[getSetColumnWidthKey()];
            if (isPreviouslySet) {
                return prev;
            }

            const copiedRowHeights = [...prev];
            if (!copiedRowHeights[rowIndex] || copiedRowHeights[rowIndex] < computedWidth) {
                copiedRowHeights[rowIndex] = computedWidth;
            }

            previouslySetColumnHeights.current[getSetColumnWidthKey()] = true;
            return copiedRowHeights;
        });
    };

    const getCellStyle = (args: GetCellStyleArgs): StyleProp<ViewStyle> => {
        const { colIndex } = args;
        const rowIndex = getRowIndex(args);
        const returnValue: StyleProp<ViewStyle> = {};
        if (!minWidth && columnWidths[colIndex]) {
            returnValue.width = columnWidths[colIndex];
        } else if (minWidth) {
            returnValue.flex = 1;
            (returnValue as any).wordBreak = 'break-word';
        }

        if (rowHeights[rowIndex]) {
            returnValue.height = rowHeights[rowIndex];
            returnValue.display = 'flex';
            returnValue.justifyContent = 'center';
            returnValue.alignItems = 'flex-start';
        }

        return returnValue;
    };

    return {
        getTableCellLayoutHandler: minWidth ? getNOOP : getTableCellLayoutHandler,
        getCellStyle,
        getTableRowLayoutHandler,
    };
};
